import React, { useState, useEffect } from 'react';
import {
  Search,
  Download,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle,
  FileText,
  AlertCircle
} from 'lucide-react';
import adminApiClient from '../services/adminApiClient';
import adminCompanyService from '../services/adminCompanyService';

const InvoicesManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('all');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companiesLoading, setCompaniesLoading] = useState(true);

  // Load companies on mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setCompaniesLoading(true);
        const data = await adminCompanyService.getPaged(null, null, 0, 1000);
        setCompanies(data?.content || []);
      } catch (err) {
        console.error('Failed to load companies:', err);
      } finally {
        setCompaniesLoading(false);
      }
    };
    loadCompanies();
  }, []);

  // Load invoices when company is selected
  useEffect(() => {
    const loadInvoices = async () => {
      if (selectedCompanyId === 'all') {
        setInvoices([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await adminApiClient.get(`/api/billing/company/${selectedCompanyId}/invoices`);
        setInvoices(data || []);
      } catch (err) {
        console.error('Failed to load invoices:', err);
        setError(err.message || 'Failed to load invoices');
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    loadInvoices();
  }, [selectedCompanyId]);

  // Calculate stats from real data
  const stats = React.useMemo(() => {
    const paidInvoices = invoices.filter(i => i.status === 'paid');
    const openInvoices = invoices.filter(i => i.status === 'open');
    const failedInvoices = invoices.filter(i => i.status === 'failed' || i.status === 'void');
    const totalRevenue = paidInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);

    return [
      {
        name: 'Total Revenue',
        value: `$${totalRevenue.toFixed(2)}`,
        icon: DollarSign,
        color: 'bg-green-500',
        subtext: selectedCompanyId === 'all' ? 'Select a company' : 'From paid invoices'
      },
      {
        name: 'Paid Invoices',
        value: paidInvoices.length.toString(),
        icon: CheckCircle,
        color: 'bg-blue-500',
        subtext: `${invoices.length > 0 ? Math.round(paidInvoices.length / invoices.length * 100) : 0}% of total`
      },
      {
        name: 'Open',
        value: openInvoices.length.toString(),
        icon: Clock,
        color: 'bg-yellow-500',
        subtext: 'Awaiting payment'
      },
      {
        name: 'Failed/Void',
        value: failedInvoices.length.toString(),
        icon: XCircle,
        color: 'bg-red-500',
        subtext: 'Requires attention'
      },
    ];
  }, [invoices, selectedCompanyId]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Paid
          </span>
        );
      case 'open':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            Open
          </span>
        );
      case 'void':
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      case 'uncollectible':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Uncollectible
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            {status}
          </span>
        );
    }
  };

  // Filter invoices based on search and status
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = searchTerm === '' ||
      invoice.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoices Management</h1>
          <p className="text-gray-600 mt-1">Track and manage all platform invoices</p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
          <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Create Invoice
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4 mb-2">
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-gray-600">{stat.name}</div>
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                </div>
              </div>
              <div className="text-xs text-gray-500">{stat.subtext}</div>
            </div>
          );
        })}
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex gap-3">
            <select
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              disabled={companiesLoading}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent min-w-[200px]"
            >
              <option value="all">Select a company...</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by invoice ID or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="open">Open</option>
              <option value="failed">Failed</option>
              <option value="void">Void</option>
              <option value="uncollectible">Uncollectible</option>
            </select>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading invoices...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 font-medium">Failed to load invoices</p>
              <p className="text-gray-600 text-sm mt-2">{error}</p>
            </div>
          </div>
        ) : selectedCompanyId === 'all' ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Select a company to view invoices</p>
              <p className="text-gray-500 text-sm mt-2">Choose a company from the dropdown above</p>
            </div>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No invoices found</p>
              <p className="text-gray-500 text-sm mt-2">
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your filters'
                  : 'This company has no invoices yet'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PDF
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-primary-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{invoice.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {invoice.description || '—'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 font-medium text-gray-900">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          {invoice.amount?.toFixed(2) || '0.00'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {invoice.date ? new Date(invoice.date).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(invoice.status)}
                      </td>
                      <td className="px-6 py-4">
                        {invoice.pdfUrl ? (
                          <a
                            href={invoice.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium text-sm"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{filteredInvoices.length}</span> invoice{filteredInvoices.length !== 1 ? 's' : ''}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Recent Activity */}
      {filteredInvoices.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Payments</h3>
            <div className="space-y-4">
              {filteredInvoices.filter(i => i.status === 'paid').slice(0, 3).length > 0 ? (
                filteredInvoices.filter(i => i.status === 'paid').slice(0, 3).map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <div className="font-medium text-gray-900">{invoice.description || invoice.id}</div>
                        <div className="text-xs text-gray-600">{invoice.id}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">${invoice.amount?.toFixed(2) || '0.00'}</div>
                      <div className="text-xs text-gray-500">{invoice.date ? new Date(invoice.date).toLocaleDateString() : '—'}</div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm">No recent payments</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Action Required</h3>
            <div className="space-y-4">
              {filteredInvoices.filter(i => i.status === 'failed' || i.status === 'void').length > 0 ? (
                filteredInvoices.filter(i => i.status === 'failed' || i.status === 'void').map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <div>
                        <div className="font-medium text-gray-900">{invoice.description || invoice.id}</div>
                        <div className="text-xs text-gray-600">{invoice.id}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">${invoice.amount?.toFixed(2) || '0.00'}</div>
                      <div className="text-xs text-gray-500 capitalize">{invoice.status}</div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm">No actions required</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesManagement;
